import { Injectable, Injector } from '@angular/core';
import {
    ApplicationStatus,
    ApplicationType,
    ApplicationUploadFilesInput,
    CodeSubcategory,
    CreateOrUpdateNiBankDetailsInput,
    CreateOrUpdateNiCourseOfferingInput,
    CreateOrUpdateNiEducationProviderInput,
    CreateOrUpdateNiPersonContactInput,
    CreateOrUpdateNiPersonInput,
    CreateOrUpdateNiSponsorshipContactInput,
    CreateOrUpdateNiSponsorshipInput,
    CreateOrUpdateNiDetailsCancelInterruptInput,

    GeneralFileDto,
    GeneralFileInfoDto,
    NiApplicationDto,
    NiBankDetailsDto,
    NiBankDetailsServiceProxy,
    NiCourseOfferingDto,
    NiCourseOfferingServiceProxy,
    NiDocumentGroup,
    NiDocumentServiceProxy,
    NiEducationProviderDto,
    NiEducationProviderServiceProxy,
    NiPersonContactDto,
    NiPersonContactServiceProxy,
    NiPersonDto,
    NiPersonServiceProxy,
    NiProfessionalDetailsDto,
    NiProfessionalDetailsServiceProxy,
    NiServiceProxy,
    NiSponsorshipContactDto,
    NiSponsorshipContactServiceProxy,
    NiSponsorshipDto,
    NiSponsorshipServiceProxy,
    NiStatus,
    PersonApplicationSummaryDto,
    SponsorshipApplicationSummaryDto,
    NiDetailsCancelInterruptServiceProxy,
    NiDetailsCancelInterruptDto
} from '@shared/service-proxies/service-proxies';
import { ApplicationService } from '../application/application.service';
import { AppComponentBase } from '@shared/common/app-component-base';
import { map, Observable, of, ReplaySubject, tap } from 'rxjs';
import { CreateOrUpdateNiProfessionalDetailsInput } from '../../../shared/service-proxies/service-proxies';
import { CodeService } from '../common/code/code.service';

@Injectable({
    providedIn: 'root',
})
export class NiService extends AppComponentBase {
    ni: NiApplicationDto;
    niService: any;
    applicationType: ApplicationType;
    isTertiaryB: boolean;

    private nisForPersonSubject = new ReplaySubject<PersonApplicationSummaryDto[]>(1);
    readonly nisForPerson$ = this.nisForPersonSubject.asObservable();

    constructor(
        private readonly applicationService: ApplicationService,
        private readonly niServiceProxy: NiServiceProxy,
        private readonly niPersonService: NiPersonServiceProxy,
        private readonly niPersonContactService: NiPersonContactServiceProxy,
        private readonly niEducationProviderService: NiEducationProviderServiceProxy,
        private readonly niDocumentService: NiDocumentServiceProxy,
        private readonly niBankDetailsService: NiBankDetailsServiceProxy,
        private readonly niProfessionalDetailsService: NiProfessionalDetailsServiceProxy,
        private readonly niCourseOfferingService: NiCourseOfferingServiceProxy,
        private readonly niSponsorshipService: NiSponsorshipServiceProxy,
        private readonly niSponsorshipContactService: NiSponsorshipContactServiceProxy,
        private readonly niDetailsCancelInterruptService: NiDetailsCancelInterruptServiceProxy,
        private readonly codeService: CodeService,
        injector: Injector
    ) {
        super(injector);
    }

    loadNisForPerson(ahv: string): Observable<PersonApplicationSummaryDto[]> {
        if (ahv && this.isGranted('Pages.Authority.Applications.Person')) {
            return this.niServiceProxy.getNisForPerson(ahv).pipe(
                tap(nis => this.nisForPersonSubject.next(nis))
            );
        } else {
            this.nisForPersonSubject.next([]);
            return of([]);
        }
    }

    public getNi(caseId: string): Observable<NiApplicationDto> {
        return this.niServiceProxy.getNi(caseId).pipe(
            tap((output: NiApplicationDto) => {
                this.ni = output;
                this.applicationType = this.ni.application.type;
                this.isTertiaryB = this.ni.niEducationProvider?.educationProviderTheoryId
                    ? this.codeService.getSubcategoryForCode(this.ni.niEducationProvider.educationProviderTheoryId) === CodeSubcategory.TertiaryB
                    : false;
            })
        );
    }

    public closeNi(): void {
        this.ni = this.ni;
    }

    get caseId(): string {
        return this.ni.application.caseId;
    }

    get status(): ApplicationStatus {
        return this.ni.application.status;
    }

    public getMyPersonNis(): Observable<PersonApplicationSummaryDto[]> {
        return this.niServiceProxy.getMyPersonNis().pipe();
    }

    public getMySponsorshipNis(): Observable<SponsorshipApplicationSummaryDto[]> {
        return this.niServiceProxy.getMySponsorshipNis().pipe();
    }

    getStepUrl(nextStep: NiStatus): string {
        return this.applicationService.getNextStep(NiService.getUrlForStep(nextStep));
    }

    static getUrlForStep(step: NiStatus): string {
        switch (step) {
            case NiStatus.Person:
                return 'person';
            case NiStatus.PersonContact:
                return 'person-contact';
            case NiStatus.Sponsorship:
                return 'sponsorship';
            case NiStatus.SponsorshipContact:
                return 'sponsorship-contact';
            case NiStatus.EducationProvider:
                return 'education-provider';
            case NiStatus.CourseOffering:
                return 'course-offering';
            case NiStatus.ProfessionalDetails:
                return 'prof-details';
            case NiStatus.ProofOfCourseCompletion:
                return 'proof-of-course-completion';
            case NiStatus.TrainingServices:
                return 'training-services';
            case NiStatus.BankDetails:
                return 'bank-details';
            case NiStatus.DetailsCancelInterrupt:
                return 'details-cancel-interrupt';
            case NiStatus.Release:
            case NiStatus.Released:
                return 'release';
            case NiStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }

    getNextStatus(status: NiStatus): Observable<NiStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.niServiceProxy.getNextStatus(this.caseId, status).pipe(map(status => {
            if (isAuthority && status === NiStatus.Release) {
                return NiStatus.FinishReview;
            }
            return status;
        }));
    }

    setNextStatus(status: NiStatus): Observable<NiStatus> {
        return this.niServiceProxy.setNextStatus(this.caseId, status).pipe();
    }

    getPerson(): Observable<NiPersonDto> {
        return this.niPersonService.getPerson(this.caseId).pipe();
    }

    createOrUpdatePerson(niPerson: NiPersonDto): Observable<NiStatus> {
        let input = new CreateOrUpdateNiPersonInput();
        input.caseId = this.caseId;
        input.niPerson = niPerson;
        return this.niPersonService.createOrUpdatePerson(input).pipe();
    }

    getPersonContact(): Observable<NiPersonContactDto> {
        return this.niPersonContactService.getPersonContact(this.caseId).pipe();
    }

    createOrUpdatePersonContact(niPersonContact: NiPersonContactDto): Observable<NiStatus> {
        let input = {
            caseId: this.caseId,
            niPersonContact: niPersonContact
        } as CreateOrUpdateNiPersonContactInput;
        return this.niPersonContactService.createOrUpdatePersonContact(input).pipe();
    }

    getEducationProvider(): Observable<NiEducationProviderDto> {
        return this.niEducationProviderService.getEducationProvider(this.caseId).pipe();
    }

    getDetailsCancelInterrupt(): Observable<NiDetailsCancelInterruptDto> {
        return this.niDetailsCancelInterruptService.getDetailsCancelInterrupt(this.caseId).pipe();
    }

    createOrUpdateDetailsCancelInterrupt(niDetailsCancelInterrupt: NiDetailsCancelInterruptDto): Observable<NiStatus> {
        let input = {
            caseId: this.caseId,
            niDetailsCancelInterrupt: niDetailsCancelInterrupt
        } as CreateOrUpdateNiDetailsCancelInterruptInput;
        return this.niDetailsCancelInterruptService.createOrUpdateDetailsCancelInterrupt(input).pipe();
    }

    createOrUpdateEducationProvider(niEducationProvider: NiEducationProviderDto): Observable<NiStatus> {
        let input = {
            caseId: this.caseId,
            niEducationProvider: niEducationProvider
        } as CreateOrUpdateNiEducationProviderInput;
        return this.niEducationProviderService.createOrUpdateEducationProvider(input).pipe();
    }

    getDocuments(status: NiStatus, isTertiaryB: boolean = false): Observable<NiDocumentGroup[]> {
        return this.niDocumentService.getDocumentsForNi(this.caseId, status, isTertiaryB).pipe();
    }

    getFile(fileId: number, step: NiStatus): Observable<GeneralFileDto> {
        return this.niDocumentService.getFile(this.caseId, step, fileId);
    }

    uploadFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.niDocumentService.uploadFiles(input);
    }

    deleteFile(id: number, step: NiStatus): Observable<void> {
        return this.niDocumentService.deleteFile(this.caseId, step, id);
    }

    getBankDetails(): Observable<NiBankDetailsDto> {
        return this.niBankDetailsService.getBankDetails(this.caseId).pipe();
    }

    createOrUpdateBankDetails(niBankDetails: NiBankDetailsDto): Observable<NiStatus> {
        let input = {
            caseId: this.caseId,
            niBankDetails: niBankDetails
        } as CreateOrUpdateNiBankDetailsInput;
        return this.niBankDetailsService.createOrUpdateBankDetails(input).pipe();
    }

    getProfessionalDetails(): Observable<NiProfessionalDetailsDto> {
        return this.niProfessionalDetailsService.getProfessionalDetails(this.caseId).pipe();
    }

    createOrUpdateProfessionalDetails(niProfessionalDetails: NiProfessionalDetailsDto): Observable<NiStatus> {
        let input = {
            caseId: this.caseId,
            niProfessionalDetails: niProfessionalDetails
        } as CreateOrUpdateNiProfessionalDetailsInput;
        return this.niProfessionalDetailsService.createOrUpdateProfessionalDetails(input).pipe();
    }

    getCourseOffering(): Observable<NiCourseOfferingDto> {
        return this.niCourseOfferingService.getCourseOffering(this.caseId).pipe();
    }

    createOrUpdateCourseOffering(niCourseOffering: NiCourseOfferingDto): Observable<NiStatus> {
        let input = {
            caseId: this.caseId,
            niCourseOffering: niCourseOffering
        } as CreateOrUpdateNiCourseOfferingInput;
        return this.niCourseOfferingService.createOrUpdateCourseOffering(input).pipe();
    }

    checkDocuments(isTertiaryB: boolean): Observable<NiStatus[]> {
        return this.niServiceProxy.validateThatAllMandatoryDocumentsHaveBeenUploaded(this.caseId, isTertiaryB);
    }

    releaseNi(comment: string, isTertiaryB: boolean): Observable<void> {
        return this.niServiceProxy.releaseNi(this.caseId, comment, isTertiaryB);
    }

    getEvaluationComments(): Observable<string> {
        return this.niServiceProxy.getEvaluationComments(this.caseId);
    }

    // downloadInvoicingRefresherCourseTemplate(): Observable<GeneralFileDto> {
    //     return this.niDocumentService.getInvoicingRefresherCourseTemplateDocument();
    // }

    getSponsorship(): Observable<NiSponsorshipDto> {
        return this.niSponsorshipService.getSponsorship(this.caseId).pipe();
    }

    createOrUpdateNiSponsorship(niSponsorship: NiSponsorshipDto): Observable<NiStatus> {
        let input = {
            caseId: this.caseId,
            niSponsorship: niSponsorship
        } as CreateOrUpdateNiSponsorshipInput;
        return this.niSponsorshipService.createOrUpdateSponsorship(input).pipe();
    }

    getSponsorshipContact(): Observable<NiSponsorshipContactDto> {
        return this.niSponsorshipContactService.getSponsorshipContact(this.caseId).pipe();
    }

    createOrUpdateSponsorshipContact(niSponsorshipContact: NiSponsorshipContactDto): Observable<NiStatus> {
        let input = {
            caseId: this.caseId,
            niSponsorshipContact: niSponsorshipContact
        } as CreateOrUpdateNiSponsorshipContactInput;
        return this.niSponsorshipContactService.createOrUpdateSponsorshipContact(input).pipe();
    }

    getPersonId(): Observable<number> {
        return this.niServiceProxy.getPersonId(this.caseId).pipe();
    }

    getSponsorshipId(): Observable<number> {
        return this.niServiceProxy.getSponsorshipId(this.caseId).pipe();
    }
}
